import React from "react"

import {
  getCompareByDisplayLabel,
  getCompareByGroupKeys,
  groupMetricOutputsByCompareBy,
  type CompareBy,
} from "@/metrics/compare-by"
import {
  MetricDistributionChart,
  MiniDistributionChart,
  ViolinPlot,
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import {
  MetricRecord,
  type DeepDiveContext,
  type MetricReviewTranscriptContext,
} from "@/metrics/runtime"

import type { Annotation } from "@/types/api/Annotation"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"
import type { RunRowLike } from "@/types/RunRow"
import { formatNumber } from "@/utils/formatting/format-number"

import { CompositeEvaluationConfig } from "../configuration/components/CompositeEvaluationConfig"
import { isEnabledAutomaticExpectedBehaviorMetric } from "../configuration/utils/isExpectedBehaviorCompositeMetric"
import {
  getCompositeResultData,
  isAllCriteriaMetPass,
  type BaseTestCaseRecord,
} from "./helpers/composite-evaluation-types"
import {
  compareCompositeValues,
  compositeAnnotationMatchesMetricOutput,
  getCompositeDisplayValue,
  getCompositeHeaderValue,
  getCompositeHumanReviewDisplayValue,
  getCompositeJudgeScore,
  getCompositeNumericValues,
  getCompositeReportingMethod,
  getCompositeVersionDiff,
  isCompositeMetricOutputCurrentVersion,
} from "./helpers/composite-evaluation-values"
import { CompositeCriteriaReview } from "./helpers/CompositeCriteriaReview"
import { CompositeEvaluationReviewSummary } from "./helpers/CompositeEvaluationReviewSummary"
import { CompositeEvaluationSubRows } from "./helpers/CompositeEvaluationSubRows"
import { CompositeJudgeScores } from "./helpers/CompositeJudgeScores"
import { NumericalHumanFeedbackButton } from "./helpers/NumericalHumanFeedbackButton"

export class CompositeEvaluationMetricRecord extends MetricRecord {
  documentation = this.metric_metadata?.question || this.description
  overviewSize = "small" as const
  // Units follow the configured reporting method so chart axis labels and
  // stat suffixes (mean / median / std) reflect percent / count / pass-fail.
  aggregate_units =
    this._getReportingMethod() === "percentage_of_criteria_met" ? "%" : ""

  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(metricOutput: {
    result?: Record<string, unknown> | null
    result_json?: Record<string, unknown> | null
  }): boolean {
    return isCompositeMetricOutputCurrentVersion(
      this.metric_metadata,
      metricOutput,
    )
  }

  getVersionDiff(metricOutput: {
    result?: Record<string, unknown> | null
    result_json?: Record<string, unknown> | null
  }): Array<{ field: string; current: string; previous: string }> {
    return getCompositeVersionDiff(this.metric_metadata, metricOutput)
  }

  private _getReportingMethod(
    resultData?: ReturnType<typeof getCompositeResultData>,
  ): string {
    return getCompositeReportingMethod(this.metric_metadata, resultData)
  }

  getKeyboardShortcutHints(): { key: string; label: string }[] {
    return [
      { key: "M", label: "Met" },
      { key: "N", label: "Not Met" },
      { key: "X", label: "N/A" },
    ]
  }

  annotationMatchesMetricOutput(
    annotation: Annotation,
    metricOutput: {
      output_type: string
      float_value: number | null
      string_value: string | null
      set_value: string[] | null
      result_json?: Record<string, unknown> | null
    },
  ): boolean {
    return compositeAnnotationMatchesMetricOutput(annotation, metricOutput)
  }

  compareValues(
    value1: {
      float_value?: number | null
      string_value?: string | null
      set_value?: string[] | null
    },
    value2: {
      float_value?: number | null
      string_value?: string | null
      set_value?: string[] | null
    },
  ): boolean {
    return compareCompositeValues(value1, value2)
  }

  renderAssignmentsTableReviewCell(
    simulationOutputId: string,
    annotations_for_metric: Annotation[],
    projectId?: string,
    metricOutput?: RunMetricOutput,
  ): React.ReactNode {
    const resultJson = getCompositeResultData(metricOutput)
    const reportingMethod = this._getReportingMethod(resultJson)

    return (
      <CompositeEvaluationReviewSummary
        annotationsForMetric={annotations_for_metric}
        metricOutput={metricOutput}
        reportingMethod={reportingMethod}
      />
    )
  }

  renderAssignmentsTableSubRows(
    simulationOutputId: string,
    annotationsForMetric: Annotation[],
    projectId?: string,
    metricOutput?: RunMetricOutput,
  ): React.ReactNode {
    return (
      <CompositeEvaluationSubRows
        metricId={this.id || ""}
        simulationOutputId={simulationOutputId}
        annotationsForMetric={annotationsForMetric}
        projectId={projectId}
        metricOutput={metricOutput}
      />
    )
  }

  hasCustomVotingUI = true
  hasHumanReviewFallback = false
  hasDeepDiveDetail = false
  ownsHumanReviewNotes = true
  // The judge-score row states the result only when every criterion carries a
  // verdict it can total. A float-only or legacy output leaves that row blank,
  // so its badge stays as the card's only report of the result.
  statesResultInCardDetail(mo: RunMetricOutput): boolean {
    const resultJson = getCompositeResultData(mo)
    const aiValues = resultJson?.individual_results?.map(
      (result) => result.result,
    )
    const criteriaCount =
      aiValues?.length ?? this.metric_metadata?.criteria?.length ?? 0
    return (
      getCompositeJudgeScore(
        aiValues,
        this._getReportingMethod(resultJson),
        criteriaCount,
      ) !== null
    )
  }

  renderHumanFeedbackValueButton(
    annotation: Annotation | null,
    onValueChange: (
      floatValue: number | null,
      stringValue: string | null,
      setValue: string[] | null,
    ) => void,
    onExplanationChange: (explanation: string | null) => void,
    otherUserAnnotations?: Annotation[],
  ): React.ReactNode {
    return (
      <NumericalHumanFeedbackButton
        annotation={annotation}
        onValueChange={onValueChange}
        onExplanationChange={onExplanationChange}
        otherUserAnnotations={otherUserAnnotations}
        minValue={1}
        maxValue={5}
      />
    )
  }

  protected _getNumericValuesForAggregation(
    metricOutputs: RunMetricOutput[],
  ): number[] {
    return getCompositeNumericValues(metricOutputs, this.metric_metadata)
  }

  getHighlightValueForMetricOutput(
    mo: RunMetricOutput,
  ): number | string | null {
    return this._getNumericValuesForAggregation([mo])[0] ?? null
  }

  getDisplayValueString(mo: MetricOutputDisplaySource): string | null {
    return getCompositeDisplayValue(mo, this.metric_metadata)
  }

  getHumanReviewDisplayValue(review: {
    output_type: string
    float_value: number | null
    string_value: string | null
    set_value: string[] | null
    json_value: Record<string, unknown> | null
  }): string | null {
    return getCompositeHumanReviewDisplayValue(review, this.metric_metadata)
  }

  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    if (mo.float_value === null) {
      return (
        <span
          className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
        >
          N/A
        </span>
      )
    }

    const resultJson = getCompositeResultData(mo)
    if (!resultJson?.summary) {
      return (
        <span
          className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
        >
          {formatNumber(mo.float_value * 100)}%
        </span>
      )
    }

    const { criteria_met_count, criteria_evaluated_count } = resultJson.summary
    const reportingMethod = this._getReportingMethod(resultJson)

    if (reportingMethod === "all_criteria_met") {
      const passed = isAllCriteriaMetPass(resultJson.summary)
      return (
        <span
          className={`${yesNoBadgeShapeClasses} ${passed ? yesNoBadgeColorClasses.success : yesNoBadgeColorClasses.danger}`}
        >
          {passed ? "Pass" : "Fail"}
        </span>
      )
    }

    if (reportingMethod === "percentage_of_criteria_met") {
      const pct =
        criteria_evaluated_count > 0
          ? (criteria_met_count / criteria_evaluated_count) * 100
          : 0
      return (
        <span
          className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
        >
          {formatNumber(pct)}%
        </span>
      )
    }

    if (reportingMethod === "count_of_criteria_met") {
      return (
        <span
          className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
        >
          {criteria_met_count}
        </span>
      )
    }

    return (
      <span
        className={`${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses.default}`}
      >
        {criteria_met_count}/{criteria_evaluated_count}
      </span>
    )
  }

  getExplanationTable(runRows: RunRowLike[]) {
    const metricId = this.id
    const allMo = runRows
      .map((r) => r.metric_outputs?.[metricId])
      .filter((mo): mo is RunMetricOutput => mo != null)
    const maxCount = allMo.reduce(
      (max, mo) =>
        Math.max(
          max,
          (getCompositeResultData(mo)?.individual_results ?? []).length,
        ),
      0,
    )
    if (maxCount <= 0) {
      return { columns: [], getRowValues: () => ({}) }
    }

    const columns: { key: string; title: string; width?: string }[] = []
    for (let i = 0; i < maxCount; i++) {
      const n = i + 1
      columns.push(
        {
          key: `explanation_${i}_result`,
          title: `Behavior ${n}`,
          width: "100px",
        },
        {
          key: `explanation_${i}_explanation`,
          title: `Behavior ${n} Explanation`,
          width: "300px",
        },
      )
    }

    return {
      columns,
      getRowValues: (runRow: RunRowLike): Record<string, string> => {
        const mo = runRow.metric_outputs?.[metricId]
        const compositeData = getCompositeResultData(mo)
        const results = compositeData?.individual_results ?? []
        const out: Record<string, string> = {}
        for (let i = 0; i < maxCount; i++) {
          const ir = results[i]
          out[`explanation_${i}_result`] = ir ? String(ir.result) : ""
          const raw = ir?.explanation
          out[`explanation_${i}_explanation`] =
            typeof raw === "string" ? raw.trim() : String(raw ?? "")
        }
        return out
      },
    }
  }

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode | null {
    const resultJson = getCompositeResultData(mo)
    if (!resultJson?.summary) {
      if (mo.float_value !== null) {
        return (
          <div className="text-2xl text-coval-text font-semibold">
            {mo.float_value?.toFixed(2)}
          </div>
        )
      }
      return null
    }

    const { criteria_met_count, criteria_evaluated_count } = resultJson.summary
    const reportingMethod = this._getReportingMethod(resultJson)

    if (reportingMethod === "all_criteria_met") {
      const passed = isAllCriteriaMetPass(resultJson.summary)
      return (
        <div
          className={`text-2xl font-semibold ${passed ? "text-coval-success-text" : "text-coval-danger-text"}`}
        >
          {passed ? "Pass" : "Fail"}
        </div>
      )
    }

    if (reportingMethod === "percentage_of_criteria_met") {
      const pct =
        criteria_evaluated_count > 0
          ? (criteria_met_count / criteria_evaluated_count) * 100
          : 0
      return (
        <div className="text-2xl text-coval-text font-semibold">
          {formatNumber(pct)}%
        </div>
      )
    }

    if (reportingMethod === "count_of_criteria_met") {
      return (
        <div className="text-2xl text-coval-text font-semibold">
          {criteria_met_count}
        </div>
      )
    }

    return (
      <div className="text-2xl text-coval-text font-semibold">
        {criteria_met_count}/{criteria_evaluated_count}
      </div>
    )
  }

  getHeaderValue(mo: RunMetricOutput[]): string | null {
    return getCompositeHeaderValue(mo, this.metric_metadata)
  }

  getOverviewNode(
    mo: RunMetricOutput[],
    options?: { highlightValue?: number | string | null },
  ): React.ReactNode | null {
    if (isEnabledAutomaticExpectedBehaviorMetric(this)) {
      return null
    }
    const showMiniChart = mo.filter((m) => m.float_value !== null).length > 1
    if (!showMiniChart) {
      return null
    }
    return (
      <MiniDistributionChart
        metricOutputs={mo}
        xAxisLabel={this.metric_display_name}
        highlightValue={options?.highlightValue}
        extractFloatValues={(outputs) =>
          this._getNumericValuesForAggregation(outputs)
        }
      />
    )
  }

  // Override so the in-depth chart routes through `_getNumericValuesForAggregation`,
  // which reads counts/pass-fail from summary instead of the raw float_value decimal.
  renderInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    _context?: {
      runRows?: RunRowLike[]
      highlightValue?: number | string | null
      onBarClick?: (filter: ChartBarFilter) => void
      activeFilters?: ChartBarFilter[]
    },
  ): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    return (
      <MetricDistributionChart
        metricOutputs={metricOutputs}
        metricRecord={this}
        showChart={!isEnabledAutomaticExpectedBehaviorMetric(this)}
        highlightValue={_context?.highlightValue}
        onBarClick={_context?.onBarClick}
        activeFilters={_context?.activeFilters}
        extractFloatValues={(outputs) =>
          this._getNumericValuesForAggregation(outputs)
        }
      />
    )
  }

  getComparedOverviewNode(
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode | null {
    if (isEnabledAutomaticExpectedBehaviorMetric(this)) {
      return null
    }
    const allGroupKeys = getCompareByGroupKeys(runRows, compareBy)
    if (!runRows.length || allGroupKeys.length === 0) {
      return null
    }
    const mo = this._getMetricOutputsFromRunRows(runRows)
    const groups = groupMetricOutputsByCompareBy(mo, runRows, compareBy)
    const reportingMethod =
      (this.metric_metadata?.reporting_method as string | undefined) ||
      "percentage_of_criteria_met"
    const isPct =
      reportingMethod === "percentage_of_criteria_met" ||
      reportingMethod === "all_criteria_met"
    const boxGroups = allGroupKeys
      .map((key) => {
        const groupMo = groups.get(key) ?? []
        const values = this._getNumericValuesForAggregation(groupMo)
        if (values.length === 0) {
          return null
        }
        return {
          id: key,
          label: getCompareByDisplayLabel(key, compareBy),
          values,
          color: compareBy.colors[key] ?? "hsl(var(--primary))",
        }
      })
      .filter((d): d is NonNullable<typeof d> => d !== null)
    if (boxGroups.length === 0) {
      return null
    }
    return (
      <ViolinPlot
        key={`${compareBy.dimension}-${compareBy.metadataKey ?? ""}`}
        groups={boxGroups}
        units={isPct ? "%" : ""}
        height={200}
      />
    )
  }

  renderComparedInDepthRunReview(
    metricOutputs: RunMetricOutput[],
    runRows: RunRowLike[],
    compareBy: CompareBy,
  ): React.ReactNode {
    return isEnabledAutomaticExpectedBehaviorMetric(this)
      ? this.renderInDepthRunReview(metricOutputs)
      : super.renderComparedInDepthRunReview(metricOutputs, runRows, compareBy)
  }

  // The card carries the whole result: per-criterion verdicts, explanations,
  // and votes all live in the human review table. Nothing is left for a
  // separate deep dive region, so there is none.
  _renderDeepDive(
    _mo: RunMetricOutput,
    _testCase: BaseTestCaseRecord,
    _dispatchMessageSelect?: (messageIndex: number | null) => void,
    _context?: DeepDiveContext,
  ): React.ReactNode {
    return null
  }

  // Both judges' totals sit in the card's top row, above the criteria table's
  // own AI and Human columns, where the card's result badge would otherwise go.
  renderDeepDiveAnnotationsPanel(
    simulationOutputId: string,
    annotationsForMetric: Annotation[],
    metricOutput?: RunMetricOutput,
    blindLabeling?: boolean,
  ): React.ReactNode {
    if (!this.id || !simulationOutputId) {
      return null
    }
    const resultJson = getCompositeResultData(metricOutput)
    return (
      <CompositeJudgeScores
        annotationsForMetric={annotationsForMetric}
        metricOutput={metricOutput}
        metadataCriteria={this.metric_metadata?.criteria}
        reportingMethod={this._getReportingMethod(resultJson)}
        showAiScore={!blindLabeling}
        labelScores={isEnabledAutomaticExpectedBehaviorMetric(this)}
      />
    )
  }

  renderHumanReviewDetail(
    simulationOutputId: string,
    annotationsForMetric: Annotation[],
    metricOutput?: RunMetricOutput,
    blindLabeling?: boolean,
    projectId?: string,
    notesNode?: React.ReactNode,
    transcriptContext?: MetricReviewTranscriptContext,
  ): React.ReactNode {
    if (!this.id || !simulationOutputId) {
      return null
    }
    const resultJson = getCompositeResultData(metricOutput)
    const reportingMethod = this._getReportingMethod(resultJson)
    const behaviorCards = isEnabledAutomaticExpectedBehaviorMetric(this)
    return (
      <div
        className={
          behaviorCards
            ? "@container/criteria flex w-full flex-col gap-3"
            : "flex w-full flex-col gap-3"
        }
      >
        {/* Each criterion is stated once, pairing the AI's verdict and
            explanation for it with the reviewer's own vote. Blind review drops
            the AI half so the reviewer isn't anchored. */}
        <CompositeCriteriaReview
          metricId={this.id}
          simulationOutputId={simulationOutputId}
          annotationsForMetric={annotationsForMetric}
          metricOutput={metricOutput}
          metadataCriteria={this.metric_metadata?.criteria}
          reportingMethod={reportingMethod}
          projectId={projectId}
          showAiResult={!blindLabeling}
          behaviorCards={behaviorCards}
          transcriptContext={transcriptContext}
        />
        {notesNode ? (
          behaviorCards ? (
            <div
              className={
                blindLabeling
                  ? "pt-3"
                  : "grid grid-cols-1 @lg/criteria:grid-cols-2"
              }
            >
              <div
                role="group"
                aria-label="Shared human review note"
                className={
                  blindLabeling ? undefined : "pt-3 @lg/criteria:col-start-2"
                }
              >
                {notesNode}
              </div>
            </div>
          ) : (
            <div className="pt-3">{notesNode}</div>
          )
        ) : null}
      </div>
    )
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    _textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  ): React.ReactNode {
    return (
      <CompositeEvaluationConfig
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    )
  }
}
